import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { communityAPI, getErrorMessage } from '../../utils/api';
import {
  MessageSquare,
  Heart,
  Share2,
  TrendingUp,
  Search,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';

const CommunityNew = () => {
  const [postContent, setPostContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [posts, setPosts] = useState([]);
  const [isPosting, setIsPosting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [trendingTopics, setTrendingTopics] = useState([]);
  const [topTraders, setTopTraders] = useState([]);
  const [expandedComments, setExpandedComments] = useState({});
  const [commentInputs, setCommentInputs] = useState({});

  const refreshSidebar = useCallback(async () => {
    try {
      const [topicsResponse, tradersResponse] = await Promise.all([
        communityAPI.getTrendingTopics(),
        communityAPI.getTopTraders(),
      ]);
      setTrendingTopics(topicsResponse.data || []);
      setTopTraders(tradersResponse.data || []);
    } catch (error) {
      console.error('Error refreshing community sidebar:', error);
    }
  }, []);

  useEffect(() => {
    const loadPosts = async () => {
      try {
        setLoading(true);
        const [postsResponse, topicsResponse, tradersResponse] =
          await Promise.all([
            communityAPI.getPosts(),
            communityAPI.getTrendingTopics(),
            communityAPI.getTopTraders(),
          ]);
        setPosts(postsResponse.data || []);
        setTrendingTopics(topicsResponse.data || []);
        setTopTraders(tradersResponse.data || []);
      } catch (error) {
        toast.error(getErrorMessage(error, 'Failed to load community posts'));
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, []);

  const formatCount = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) {
      return value;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return String(num);
  };

  const formatVolume = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) {
      return value;
    }
    return `$${num.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  };

  const avatarColors = ['bg-orange-500', 'bg-blue-500', 'bg-yellow-500'];

  const formatTime = (value) => {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }
    return date.toLocaleString();
  };

  const visiblePosts = useMemo(() => {
    if (!searchTerm.trim()) {
      return posts;
    }
    const term = searchTerm.toLowerCase();
    return posts.filter((post) =>
      `${post.author_name} ${post.handle} ${post.content}`
        .toLowerCase()
        .includes(term)
    );
  }, [posts, searchTerm]);

  const handlePost = async () => {
    const content = postContent.trim();
    if (!content) {
      return;
    }
    try {
      setIsPosting(true);
      const response = await communityAPI.createPost({ content });
      setPosts((prev) => [response.data, ...prev]);
      setPostContent('');
      toast.success('Post published');
      await refreshSidebar();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create post'));
    } finally {
      setIsPosting(false);
    }
  };

  const handleLike = async (postId) => {
    try {
      const response = await communityAPI.toggleLike(postId);
      setPosts((prev) =>
        prev.map((post) =>
          post.id === postId
            ? {
                ...post,
                likes: response.data.likes,
                liked_by_user: response.data.liked_by_user,
              }
            : post
        )
      );
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update like'));
    }
  };

  const handleComment = async (postId) => {
    const content = (commentInputs[postId] || '').trim();
    if (!content) return;
    try {
      const response = await communityAPI.addComment(postId, {
        content,
      });
      setPosts((prev) =>
        prev.map((post) =>
          post.id === postId
            ? { ...post, comments: response.data.comments || [] }
            : post
        )
      );
      setCommentInputs((prev) => ({ ...prev, [postId]: '' }));
      toast.success('Comment added');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add comment'));
    }
  };

  const toggleComments = (postId) => {
    setExpandedComments((prev) => ({
      ...prev,
      [postId]: !prev[postId],
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          .loading-spinner {
            width: 48px;
            height: 48px;
            border: 4px solid #e5e7eb;
            border-top-color: #062136;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
        `}</style>
        <div className="loading-spinner"></div>
      </div>
    )
  }

  return (
    <div
      className="space-y-6 px-4 sm:px-6 lg:pl-10 lg:pr-6 py-6 sm:py-10 animate-fadeIn bg-[#F9F9F9] dark:bg-transparent"
      data-testid="community-page"
    >
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
          Community
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Connect with fellow crypto enthusiasts
        </p>
      </div>

      {/* Two Column Layout - sidebar first on mobile (column), main feed left on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column - Main Feed (order-2 on mobile so it appears below search/trending) */}
        <div className="order-2 lg:order-1 lg:col-span-8 space-y-4">
          {/* Create Post Section */}
          <Card className="p-4 sm:p-5 border-none shadow-none">
            <div className="flex flex-col gap-2 items-start">
              <div className="w-full flex items-start sm:items-center gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#21bcfe] flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                  A
                </div>
                <Input
                  placeholder="What's happening in the crypto world?"
                  value={postContent}
                  onChange={(e) => setPostContent(e.target.value)}
                  className="flex-1 min-w-0 border-gray-200 dark:border-gray-700"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handlePost();
                    }
                  }}
                />
              </div>
              <Button
                onClick={handlePost}
                className="bg-[#062136] hover:bg-[#062136]/90 text-white font-semibold px-6 rounded-lg self-end"
                disabled={!postContent.trim() || isPosting}
              >
                {isPosting ? 'Posting...' : 'Post'}
              </Button>
            </div>
          </Card>

          {/* Posts Feed */}
          <div className="space-y-2">
            {visiblePosts.map((post) => (
              <Card
                key={post.id}
                className="p-4 sm:p-5 border-none shadow-none border-b border-gray-200 dark:border-gray-800 rounded-xl first:rounded-t-lg last:rounded-b-lg overflow-hidden"
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  {/* Avatar */}
                  <div
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#21bcfe] flex items-center justify-center text-white font-bold flex-shrink-0 overflow-hidden"
                  >
                    {post.avatar_url ? (
                      <img
                        src={post.avatar_url}
                        alt={post.author_name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span className="text-white font-bold">
                        {post.author_name?.charAt(0) || 'A'}
                      </span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 overflow-hidden">
                    {/* User Info - name on first line, handle + timestamp on second (handle ellipsized if long) */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:flex-wrap gap-0.5 sm:gap-x-2 mb-2">
                      <h3 className="font-semibold text-gray-900 dark:text-white shrink-0">
                        {post.author_name}
                      </h3>
                      <div className="flex items-center min-w-0 gap-x-1.5">
                        <span
                          className="text-gray-500 dark:text-gray-400 text-sm truncate max-w-[140px] sm:max-w-[200px]"
                          title={post.handle}
                        >
                          {post.handle}
                        </span>
                        <span className="text-gray-400 dark:text-gray-500 text-sm shrink-0">
                          · {formatTime(post.created_at)}
                        </span>
                      </div>
                    </div>

                    {/* Post Content */}
                    <p className="text-gray-900 dark:text-white mb-4 text-base break-words">
                      {post.content}
                    </p>

                    {/* Engagement Metrics */}
                    <div className="flex flex-wrap items-center gap-x-4 sm:gap-x-6 gap-y-2 text-gray-500 dark:text-gray-400">
                      <button
                        className={`flex items-center space-x-2 transition-colors ${post.liked_by_user
                          ? 'text-blue-600'
                          : 'hover:text-blue-600 dark:hover:text-blue-400'
                          }`}
                        onClick={() => handleLike(post.id)}
                      >
                        <Heart className="w-5 h-5" />
                        <span className="text-sm font-medium">
                          {post.likes}
                        </span>
                      </button>
                      <button
                        className="flex items-center space-x-2 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                        onClick={() => setExpandedComments((prev) => ({ ...prev, [post.id]: !prev[post.id] }))}
                      >
                        <MessageSquare className="w-5 h-5" />
                        <span className="text-sm font-medium">
                          {(post.comments || []).length}
                        </span>
                      </button>
                      <button className="flex items-center space-x-2 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                        <Share2 className="w-5 h-5" />
                        <span className="text-sm font-medium">
                          {post.shares}
                        </span>
                      </button>
                    </div>

                    {(post.comments || []).length > 0 || expandedComments[post.id] ? (
                      <div className="mt-4">
                        <button
                          className="text-sm font-medium text-blue-600 hover:text-blue-700"
                          onClick={() => toggleComments(post.id)}
                        >
                          {expandedComments[post.id]
                            ? 'Hide comments'
                            : 'View comments'}
                        </button>
                        {expandedComments[post.id] && (
                          <div className="mt-3 space-y-3">
                            {(post.comments || []).map((comment) => (
                              <div
                                key={comment.id}
                                className="rounded-lg bg-gray-50 px-3 py-2"
                              >
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-semibold text-gray-900">
                                    {comment.author_name}
                                  </span>
                                  <span className="text-xs text-gray-400">
                                    {formatTime(comment.created_at)}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-700 mt-1">
                                  {comment.content}
                                </p>
                              </div>
                            ))}
                            {/* Inline comment input */}
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mt-2">
                              <Input
                                type="text"
                                placeholder="Write a comment..."
                                value={commentInputs[post.id] || ''}
                                onChange={e => setCommentInputs(prev => ({ ...prev, [post.id]: e.target.value }))}
                                className="flex-1 min-w-0"
                                onKeyDown={e => {
                                  if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleComment(post.id);
                                  }
                                }}
                              />
                              <Button
                                size="sm"
                                className="bg-[#21bcfe] text-white px-4 py-2"
                                disabled={!commentInputs[post.id] || !commentInputs[post.id].trim()}
                                onClick={() => handleComment(post.id)}
                              >
                                Comment
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Right Column - Sidebar (order-1 on mobile so search/trending appear at top) */}
        <div className="order-1 lg:order-2 lg:col-span-4 space-y-6">
          {/* Search Community */}
          <Card className="border-none shadow-none bg-transparent">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 bg-transparent" />
              <Input
                placeholder="Search community"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-gray-200 dark:border-gray-700"
              />
            </div>
          </Card>

          {/* Trending Topics */}
          <Card className="p-5 border-none shadow-none">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Trending Topics
            </h2>
            <div className="space-y-3">
              {trendingTopics.map((topic) => (
                <div
                  key={topic.tag}
                  className="flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 p-2 rounded-lg cursor-pointer transition-colors"
                >
                  <span className="text-[#21bcfe] dark:text-blue-400 font-medium text-sm">
                    {topic.tag}
                  </span>
                  <span className="text-gray-500 dark:text-gray-400 text-sm">
                    {formatCount(topic.posts)} posts
                  </span>
                </div>
              ))}
            </div>
          </Card>

          {/* Top Traders */}
          {/* <Card className="p-5 border-none shadow-none"> */}
            {/* <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
              <Users className="w-5 h-5 mr-2" />
              Top Traders
            </h2>
            <div className="space-y-3 mb-4">
              {topTraders.map((trader, index) => (
                <div
                  key={trader.name}
                  className="flex items-center space-x-3 hover:bg-gray-50 dark:hover:bg-gray-800 p-2 rounded-lg cursor-pointer transition-colors"
                >
                  <div
                    className={`w-10 h-10 rounded-full ${avatarColors[index % avatarColors.length]} flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}
                  >
                    {(trader.name || 'U').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 dark:text-white font-medium text-sm truncate">
                      {trader.name}
                    </p>
                  </div>
                  <span className="text-green-600 dark:text-green-400 text-sm font-semibold flex-shrink-0">
                    {formatVolume(trader.volume)}
                  </span>
                </div>
              ))}
            </div> */}
            {/* <Button
              variant="outline"
              className="w-full border-gray-300 dark:border-gray-700 text-black dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-800 bg-gray-100"
            >
              View Leaderboard
            </Button> */}
          {/* </Card> */}
        </div>
      </div>
    </div>
  );
};

export default CommunityNew;
